import React, { useState } from 'react';
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Tooltip,
  Stack,
  TablePagination,
  FormControlLabel,
  Switch,
} from '@mui/material';
import dayjs from 'dayjs';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CollectionsIcon from '@mui/icons-material/Collections';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import { t } from 'i18next';
import PreviewPicture from '../../components/PreviewPic/PreviewPicture';

// Helpers
const fmtDate = (d) => (d ? dayjs(d).format('YYYY-MM-DD') : '-');
const statusColor = (s) => {
  switch (s) {
    case 'Loaded':
      return 'success';
    case 'Arrived':
      return 'warning';
    case 'Not Arrived':
      return 'info';
    case 'Departed':
      return 'error';
    default:
      return 'default';
  }
};
const Ellipsis = ({ children, width = 160 }) => (
  <Box sx={{ maxWidth: width, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={typeof children === 'string' ? children : undefined}>
    {children ?? '-'}
  </Box>
);



const getContainerVehicleItems = (container) => {
  const shipmentItemVehicles = (container?.shipmentItems || [])
    .filter((item) => item?.vehicle)
    .map((item) => ({
      id: item.id,
      vehicle: item.vehicle,
      billOfLadingFile: item.billOfLadingFile || [],
      client: item.client || {
        id: item.clientId ?? item.vehicle?.clientId,
        name: item.clientName ?? item.vehicle?.clientName,
        company: item.clientCompany ?? item.vehicle?.clientCompany,
      },
    }));

  if (shipmentItemVehicles.length) return shipmentItemVehicles;

  return (container?.vehicles || []).map((vehicle) => ({
    id: vehicle?.id,
    vehicle,
    billOfLadingFile: vehicle?.billOfLadingFile || [],
    client: {
      id: vehicle?.clientId,
      name: vehicle?.clientName,
      company: vehicle?.clientCompany,
    },
  }));
};

const getVehicleValue = (value) => value?.name || value || '-';
const normalizeId = (value) => (value === null || value === undefined ? '' : String(value));
const getVehicleItemClientId = (item) => item?.client?.id ?? item?.clientId ?? item?.vehicle?.clientId;
const filterVehicleItemsByClient = (vehicleItems = [], currentClientId) => {
  const clientId = normalizeId(currentClientId);

  if (!clientId) return [];

  return vehicleItems.filter((item) => normalizeId(getVehicleItemClientId(item)) === clientId);
};
const getClientLabel = (client) => {
  if (!client?.name && !client?.company) return '-';
  return [client?.name, client?.company ? `(${client.company})` : ''].filter(Boolean).join(' ');
};
const getUniqueClients = (clients = []) => {
  const map = new Map();

  clients
    .filter((client) => client?.name || client?.company)
    .forEach((client) => {
      const key = client?.id || `${client?.name || ''}-${client?.company || ''}`;
      if (!map.has(key)) map.set(key, client);
    });

  return Array.from(map.values());
};

const renderContainerClients = (row, vehicleItems, isClientPortal = false) => {
  const clients = getUniqueClients([
    isClientPortal
      ? null
      : {
          name: row?.clientName,
          company: row?.clientCompanyName,
        },
    ...(vehicleItems || []).map((item) => item?.client),
  ]);
  const primaryClient = clients[0];
  const secondaryClients = clients.slice(1);

  return (
    <Box sx={{ minWidth: 180 }}>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: 180,
        }}
        title={primaryClient?.name || '-'}
      >
        {primaryClient?.name || '-'}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{
          mt: 0.25,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: 180,
        }}
        title={primaryClient?.company || '-'}
      >
        {primaryClient?.company ? `"${primaryClient.company}"` : '-'}
      </Typography>
      {secondaryClients.length > 0 && row.shipmentMode === 'LCL' && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75, alignItems: 'center' }}>
          {secondaryClients.map((client) => (
            <Chip
              key={client?.id || `${client?.name || ''}-${client?.company || ''}`}
              size="small"
              label={client?.name || client?.company || 'N/A'}
              variant="outlined"
              sx={{
                borderColor: 'secondary.main',
                color: 'secondary.main',
                backgroundColor: 'transparent',
                fontSize: 11,
                height: 24,
                textTransform: 'capitalize',
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// Contract:
// props:
// - data: Array of container objects (API content)
// - onEdit: (id) => void
// - onArrived: (row) => void
// - onDelete: (row) => void

const VehiclesSubTable = ({ vehicleItems = [] }) => {
  const [openPreview, setOpenPreview] = useState(false);
  const [files, setFiles] = useState([]);


  const openFiles = (arr) => {
    setFiles(arr || []);
    setOpenPreview(true);
  };

  const closePreview = () => {
    setOpenPreview(false);
    setFiles([]);
  };

  return (
    <Box>
      <Table size="small" aria-label="vehicles">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'primary.main' }}>
            <TableCell sx={{ color: 'secondary.main' }}>ID</TableCell>
            <TableCell sx={{ color: 'secondary.main' }}>VIN</TableCell>
            <TableCell sx={{ color: 'secondary.main' }}>Client</TableCell>
            <TableCell sx={{ color: 'secondary.main' }}>Make</TableCell>
            <TableCell sx={{ color: 'secondary.main' }}>Model</TableCell>
            <TableCell sx={{ color: 'secondary.main' }}>Type</TableCell>
            <TableCell sx={{ color: 'secondary.main' }} align="right">Bill of Lading</TableCell>
            <TableCell sx={{ color: 'secondary.main' }} align="right">Pictures</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(vehicleItems || []).map((item, idx) => {
            const v = item?.vehicle || {};
            const clientLabel = getClientLabel(item?.client);
            const blFiles = item?.billOfLadingFile || [];
            const vPics = v?.warehousePictures || [];
            const blCount = blFiles.length;
            const vCount = vPics.length;
            return (
              <TableRow key={`veh-${v?.id || idx}`} hover>
                <TableCell>{v?.id}</TableCell>
                <TableCell><Ellipsis width={200}>{v?.vin}</Ellipsis></TableCell>
                <TableCell><Ellipsis width={220}>{clientLabel}</Ellipsis></TableCell>
                <TableCell><Ellipsis width={160}>{getVehicleValue(v?.make)}</Ellipsis></TableCell>
                <TableCell><Ellipsis width={160}>{getVehicleValue(v?.model)}</Ellipsis></TableCell>
                <TableCell><Ellipsis width={140}>{getVehicleValue(v?.vehicleType)}</Ellipsis></TableCell>
                <TableCell align="right">
                  <Tooltip title={`${blCount} File${blCount === 1 ? '' : 's'}`}>
                    <span>
                      <IconButton onClick={() => openFiles(blFiles)} aria-label="Bill of Lading File" disabled={blCount === 0} size="small">
                        <PictureAsPdfIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {blCount > 0 && <Chip size="small" label={blCount} sx={{ ml: 0.5 }} />}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={`${vCount} picture${vCount === 1 ? '' : 's'}`}> 
                    <span>
                      <IconButton onClick={() => openFiles(vPics)} aria-label="view" disabled={vCount === 0} size="small">
                        <CollectionsIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {vCount > 0 && <Chip size="small" label={vCount} sx={{ ml: 0.5 }} />}
                </TableCell>
              </TableRow>
            );
          })}
          {(!vehicleItems || vehicleItems.length === 0) && (
            <TableRow>
              <TableCell colSpan={8}>
                <Typography variant="body2" color="text.secondary">No vehicles</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {openPreview && (
        <PreviewPicture open={openPreview} handleClose={closePreview} label={'PreviewPicture'} file={files} />
      )}
    </Box>
  );
};

const Row = ({ row, accountType, currentClientId }) => {
  const [open, setOpen] = useState(false);
  const pics = row?.pictures || [];
  const isClientPortal = accountType === 'client';
  const isLclShipment = row?.shipmentMode?.toString().toUpperCase() === 'LCL';
  const blFiles = isLclShipment ? [] : row?.billOfLadingFile || [];
  const allVehicleItems = getContainerVehicleItems(row);
  const vehicleItems = isClientPortal ? filterVehicleItemsByClient(allVehicleItems, currentClientId) : allVehicleItems;

  const [openPreview, setOpenPreview] = useState({open : false , label:"",files:[]});
  
  const closePics = () => setOpenPreview({open : false , label:"",files:[]});

  return (
    <>
      <TableRow hover>
        <TableCell>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title={open ? 'Collapse' : 'Expand'}>
              <IconButton aria-label="expand row" size="small" onClick={() => setOpen((p) => !p)}>
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </Tooltip>
            <Typography variant="body2">{row?.id}</Typography>
          </Stack>
        </TableCell>
        <TableCell>
          <Chip size="small" label={row?.containerStatus || '-'} color={statusColor(row?.containerStatus)} />
        </TableCell>
        <TableCell><Ellipsis width={180}>{row?.containerNumber || '-'}</Ellipsis></TableCell>
        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><Ellipsis width={140}>{row?.sealNumber || '-'}</Ellipsis></TableCell>
        
        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}><Ellipsis width={160}>{row?.bookingNumber || '-'}</Ellipsis></TableCell>
        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{renderContainerClients(row, vehicleItems, isClientPortal)}</TableCell>
        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}><Ellipsis width={200}>{row?.destination || '-'}</Ellipsis></TableCell>
        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><Ellipsis width={160}>{row?.vesselName || '-'}</Ellipsis></TableCell>
        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}><Ellipsis width={140}>{row?.voyageNumber || '-'}</Ellipsis></TableCell>
        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}><Ellipsis width={180}>{row?.shippingLineName || '-'}</Ellipsis></TableCell>
        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{fmtDate(row?.estimatedSailingDate)}</TableCell>
        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{fmtDate(row?.estimatedArrivalDate)}</TableCell>

        <TableCell align="right">
          {isLclShipment ? (
            <Typography variant="body2" color="text.secondary">-</Typography>
          ) : (
            <>
              <Tooltip title={`${blFiles.length} File${blFiles.length === 1 ? '' : 's'}`}>
                <span>
                  <IconButton onClick={()=>setOpenPreview({open:true ,label:"Bill of Lading File",files:blFiles})} aria-label="Bill of Lading File" size="small" disabled={blFiles.length === 0}>
                    <PictureAsPdfIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              {blFiles.length > 0 && <Chip size="small" label={blFiles.length} sx={{ ml: 0.5 }} />}
            </>
          )}
        </TableCell>


        <TableCell align="right">
          <Tooltip title={`${pics.length} picture${pics.length === 1 ? '' : 's'}`}>
            <span>
              <IconButton onClick={()=>setOpenPreview({open:true,label:"Container Pictures",files:pics})} aria-label="Container pictures" size="small" disabled={pics.length === 0}>
                <CollectionsIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {pics.length > 0 && <Chip size="small" label={pics.length} sx={{ ml: 0.5 }} />}
        </TableCell>
        <TableCell>
          <Chip size="small" label={vehicleItems.length} />
        </TableCell>
       
      </TableRow>
      <TableRow>
        <TableCell sx={{ paddingBottom: 0, paddingTop: 0 }} colSpan={15}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 2, width: '100%' }}>
              <Typography variant="subtitle1" gutterBottom component="div">
                Vehicles ({vehicleItems.length})
              </Typography>
              <Divider sx={{ my: 1 }} />
              <VehiclesSubTable vehicleItems={vehicleItems} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {openPreview && (
        <PreviewPicture open={openPreview.open} handleClose={closePics} label={openPreview.label} file={openPreview.files} />
      )}
    </>
  );
};

const TabelContainers = ({
  data = [],
  total = 0,
  page = 0,
  rowsPerPage = 5,
  accountType,
  currentClientId,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const [dense, setDense] = useState(true);
  const visibleData = data || [];
  const paginationTotal = Number(total) || 0;

  return (
    <Box sx={{ width: '100%' }}>
      <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }} aria-labelledby="containersTableTitle" size={dense ? 'small' : 'medium'}>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'secondary.main', width: 110 }}>ID</TableCell>
              <TableCell sx={{ color: 'secondary.main', width: 120 }}>Status</TableCell>
              <TableCell sx={{ color: 'secondary.main', width: 180 }}>Container No</TableCell>
              <TableCell sx={{ color: 'secondary.main', width: 140, display: { xs: 'none', md: 'table-cell' } }}>Seal No</TableCell>
              <TableCell sx={{ color: 'secondary.main', width: 160, display: { xs: 'none', lg: 'table-cell' } }}>Booking</TableCell>
              <TableCell sx={{ color: 'secondary.main', width: 180, display: { xs: 'none', sm: 'table-cell' } }}>Client</TableCell>
              <TableCell sx={{ color: 'secondary.main', width: 200, display: { xs: 'none', lg: 'table-cell' } }}>Destination</TableCell>
              <TableCell sx={{ color: 'secondary.main', width: 160, display: { xs: 'none', md: 'table-cell' } }}>Vessel</TableCell>
              <TableCell sx={{ color: 'secondary.main', width: 140, display: { xs: 'none', lg: 'table-cell' } }}>Voyage</TableCell>
              <TableCell sx={{ color: 'secondary.main', width: 180, display: { xs: 'none', lg: 'table-cell' } }}>Shipping Line</TableCell>
              <TableCell sx={{ color: 'secondary.main', width: 120, display: { xs: 'none', md: 'table-cell' } }}>ETD</TableCell>
              <TableCell sx={{ color: 'secondary.main', width: 120, display: { xs: 'none', md: 'table-cell' } }}>ETA</TableCell>
              <TableCell sx={{ color: 'secondary.main', width: 120 }} align="right">Bill of Lading</TableCell>
               <TableCell sx={{ color: 'secondary.main', width: 120 }} align="right">Pictures</TableCell>

              <TableCell sx={{ color: 'secondary.main', width: 110 }}>Vehicles</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleData.map((row, index) => (
              <Row
                key={row?.id || index}
                row={row}
                accountType={accountType}
                currentClientId={currentClientId}
              />
            ))}
            {visibleData.length === 0 && (
              <TableRow>
                <TableCell colSpan={15}>
                  <Typography variant="body2" color="text.secondary">No containers</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      



  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, mt: 1 }}>

<TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={paginationTotal}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        SelectProps={{ inputProps: { 'aria-label': 'rows per page' }, native: true }}
      />


        <FormControlLabel control={<Switch checked={dense} onChange={() => setDense((p) => !p)} />} label={t('Densepadding')} />
      </Box>

    </Box>
  );
};

export default TabelContainers;
